import { motion, AnimatePresence } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Menu, X, Moon, Sun, ChevronDown, Palette, Smartphone, Globe, Megaphone, Info, Mail, Users, Newspaper, Briefcase, Cloud, BarChart3 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import LogoImage from '../../assets/images/LogoImage.png';

const TopNavigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const { isDarkMode, toggleTheme } = useTheme();
    const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('token'));
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        setIsLoggedIn(!!localStorage.getItem('token'));
    }, [location]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const toggleMenu = () => setIsOpen(!isOpen);

    const handleLogout = () => {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        navigate('/');
        setIsOpen(false);
    };

    const displayLinks = [
        {
            name: 'Services',
            path: '/services',
            hasDropdown: true,
            sublinks: [
                { name: 'Graphics Designing', path: '/graphic-designing', icon: <Palette size={18} />, desc: 'Stunning visual branding and creative assets.' },
                { name: 'Mobile App Dev', path: '/mobile-app-development', icon: <Smartphone size={18} />, desc: 'Bespoke iOS and Android mobile solutions.' },
                { name: 'Web Development', path: '/web-development', icon: <Globe size={18} />, desc: 'Scalable, high-performance web applications.' },
                { name: 'Digital Marketing', path: '/digital-marketing', icon: <Megaphone size={18} />, desc: 'Data-driven growth and visibility strategies.' },
                { name: 'SaaS Software', path: '/saas-software', icon: <Cloud size={18} />, desc: 'Enterprise SaaS platforms and cloud solutions.' },
                { name: 'Data & BI', path: '/data-intelligence', icon: <BarChart3 size={18} />, desc: 'Business intelligence and data analytics.' }
            ]
        },
        {
            name: 'Pricing',
            path: '/pricing',
            hasDropdown: false,
        },
        {
            name: 'Company',
            path: '/about',
            hasDropdown: true,
            sublinks: [
                { name: 'About Us', path: '/about', icon: <Info size={18} />, desc: 'Learn about our mission and vision.' },
                { name: 'Contact Us', path: '/contact', icon: <Mail size={18} />, desc: 'Get in touch with our team today.' },
                { name: 'Our Team', path: '/team', icon: <Users size={18} />, desc: 'Meet the experts behind our success.' }
            ]
        },
        {
            name: 'Resources',
            path: '/blogs',
            hasDropdown: true,
            sublinks: [
                { name: 'Latest News', path: '/blogs', icon: <Newspaper size={18} />, desc: 'Stay updated with our latest insights.' },
                { name: 'Brand Assets', path: '/brand-assets', icon: <Briefcase size={18} />, desc: 'Logo, colors, and media kits.' }
            ]
        },
    ];

    const [activeDropdown, setActiveDropdown] = useState(null);

    return (
        <nav className={`bg-white dark:bg-[#021E23] border-b border-gray-200 dark:border-white/5 fixed w-full z-50 transition-all duration-300 font-sora ${scrolled ? 'shadow-sm' : ''}`}>
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-14 sm:h-[72px]">
                    <div className="flex-shrink-0 flex items-center cursor-pointer mr-2 sm:mr-12" onClick={() => navigate('/')}>
                        <img src={LogoImage} alt="Lockie Visuals" className="h-8 sm:h-10 w-auto" />
                    </div>

                    <div className="hidden lg:flex items-center space-x-1">
                        {displayLinks.map((link) => (
                            <div
                                key={link.name}
                                className="relative py-[26px]"
                                onMouseEnter={() => setActiveDropdown(link.name)}
                                onMouseLeave={() => setActiveDropdown(null)}
                            >
                                <Link
                                    to={link.path}
                                    className={`flex items-center gap-1.5 px-3 xl:px-4 py-2 text-[14px] xl:text-[15px] font-medium transition-colors hover:text-orange-500 dark:hover:text-white ${location.pathname === link.path || (activeDropdown === link.name)
                                        ? 'text-orange-500 dark:text-white'
                                        : 'text-gray-600 dark:text-gray-300'
                                        }`}
                                >
                                    {link.name}
                                    {link.hasDropdown && (
                                        <motion.div
                                            animate={{ rotate: activeDropdown === link.name ? 180 : 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <ChevronDown size={14} className="text-gray-400 group-hover:text-orange-500 dark:group-hover:text-white transition-colors" />
                                        </motion.div>
                                    )}
                                </Link>

                                {link.hasDropdown && activeDropdown === link.name && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                        transition={{ duration: 0.2, ease: "easeOut" }}
                                        className="absolute left-0 top-[65px] w-72 z-50 pt-2"
                                    >
                                        <div className="bg-white/95 dark:bg-[#021E23]/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-100 dark:border-white/10 py-3 overflow-hidden">
                                            {link.sublinks.map((sub, sIdx) => (
                                                <motion.div
                                                    key={sub.name}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: sIdx * 0.05 }}
                                                >
                                                    <Link
                                                        to={sub.path}
                                                        className="flex items-start gap-4 px-5 py-4 text-gray-600 dark:text-gray-300 hover:bg-orange-500/5 dark:hover:bg-orange-500/10 hover:text-orange-500 dark:hover:text-white transition-all group/sub"
                                                    >
                                                        <div className="mt-0.5 p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-xl group-hover/sub:bg-orange-500 group-hover/sub:text-white transition-all duration-300 shadow-sm">
                                                            {sub.icon || <div className="w-[18px] h-[18px]" />}
                                                        </div>
                                                        <div>
                                                            <div className="text-[14.5px] font-bold mb-1 tracking-tight">{sub.name}</div>
                                                            {sub.desc && <div className="text-[12px] text-gray-500 dark:text-gray-400 leading-snug">{sub.desc}</div>}
                                                        </div>
                                                    </Link>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="hidden lg:flex items-center gap-4 xl:gap-6">
                        {isLoggedIn ? (
                            <>
                                <Link
                                    to="/admin/dashboard"
                                    className="text-[14px] xl:text-[15px] font-medium text-gray-600 dark:text-gray-300 hover:text-orange-500 dark:hover:text-white transition-colors whitespace-nowrap"
                                >
                                    Dashboard
                                </Link>
                                <span className="text-gray-300 dark:text-gray-600 text-sm">|</span>
                                <button
                                    onClick={handleLogout}
                                    className="px-4 xl:px-5 py-1.5 xl:py-2 text-[14px] xl:text-[15px] font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors whitespace-nowrap"
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            <Link to="/contact">
                                <button className="px-5 xl:px-6 py-2 xl:py-2.5 text-[14px] xl:text-[15px] font-bold text-white bg-orange-500 rounded-full hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/20 active:scale-95 whitespace-nowrap">
                                    Contact Sales
                                </button>
                            </Link>
                        )}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                            aria-label="Toggle Theme"
                        >
                            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                        </button>
                    </div>

                    <div className="lg:hidden flex items-center gap-1 sm:gap-2">
                        <button
                            onClick={toggleTheme}
                            className="p-2 sm:p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400 transition-colors"
                            aria-label="Toggle Theme"
                        >
                            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                        </button>
                        <button
                            onClick={toggleMenu}
                            className="p-2 sm:p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/5 text-gray-700 dark:text-gray-200 focus:outline-none transition-all active:scale-95 relative"
                            aria-label={isOpen ? 'Close menu' : 'Open menu'}
                        >
                            {isOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="lg:hidden bg-white/95 dark:bg-[#021E23]/95 backdrop-blur-xl border-t border-gray-100 dark:border-white/10 overflow-hidden shadow-2xl"
                        style={{ maxHeight: 'calc(100vh - 56px)' }}
                    >
                        <div className="overflow-y-auto px-3 sm:px-4 pt-3 sm:pt-4 pb-6 sm:pb-8 space-y-1 sm:space-y-2">
                            {displayLinks.map((link) => (
                                <div key={link.name} className="space-y-1">
                                    <button
                                        onClick={() => link.hasDropdown ? setActiveDropdown(activeDropdown === link.name ? null : link.name) : (setIsOpen(false), navigate(link.path))}
                                        className={`flex items-center justify-between w-full px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl text-[15px] sm:text-[16px] font-bold transition-all ${location.pathname === link.path
                                            ? 'text-orange-500 bg-orange-500/5'
                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5'
                                            }`}
                                    >
                                        <span>{link.name}</span>
                                        {link.hasDropdown && (
                                            <motion.div
                                                animate={{ rotate: activeDropdown === link.name ? 180 : 0 }}
                                                transition={{ duration: 0.2 }}
                                            >
                                                <ChevronDown size={16} className="text-gray-400" />
                                            </motion.div>
                                        )}
                                    </button>

                                    <AnimatePresence>
                                        {link.hasDropdown && activeDropdown === link.name && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                                exit={{ opacity: 0, height: 0 }}
                                                className="pl-3 sm:pl-4 space-y-0.5 sm:space-y-1 overflow-hidden"
                                            >
                                                {link.sublinks.map((sub) => (
                                                    <Link
                                                        key={sub.name}
                                                        to={sub.path}
                                                        onClick={() => setIsOpen(false)}
                                                        className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 text-[13px] sm:text-[14.5px] font-medium text-gray-500 dark:text-gray-400 hover:text-orange-500 transition-colors"
                                                    >
                                                        <div className="p-1 sm:p-1.5 bg-gray-50 dark:bg-white/5 rounded-lg text-gray-400">
                                                            {sub.icon}
                                                        </div>
                                                        {sub.name}
                                                    </Link>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ))}

                            <div className="pt-4 sm:pt-6 mt-3 sm:mt-4 border-t border-gray-100 dark:border-white/10 space-y-2 sm:space-y-3">
                                {isLoggedIn ? (
                                    <>
                                        <Link
                                            to="/admin/dashboard"
                                            onClick={() => setIsOpen(false)}
                                            className="flex items-center justify-center w-full py-3 sm:py-3.5 rounded-xl text-[15px] sm:text-[16px] font-bold text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-white/10 hover:border-orange-500/50 transition-colors"
                                        >
                                            Dashboard
                                        </Link>
                                        <button
                                            onClick={handleLogout}
                                            className="w-full py-3 sm:py-3.5 rounded-xl text-[15px] sm:text-[16px] font-bold text-red-500 bg-red-500/10"
                                        >
                                            Logout
                                        </button>
                                    </>
                                ) : (
                                    <Link
                                        to="/contact"
                                        onClick={() => setIsOpen(false)}
                                        className="flex items-center justify-center w-full py-3 sm:py-3.5 rounded-xl text-[15px] sm:text-[16px] font-bold text-white bg-orange-500 shadow-lg shadow-orange-500/20"
                                    >
                                        Contact Sales
                                    </Link>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default TopNavigation;
